import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import TransactionProgressIndicator from './TransactionProgressIndicator';
import { useSettledTransactions } from './useSettledTransactions';

function OnboardingProgress({ checkStatus, username, onContinueToDashboard, onConnectAccounts, hasAccounts }) {
  const [ready, setReady] = useState(false);
  const [showSkipOptions, setShowSkipOptions] = useState(false);
  const navigate = useNavigate();
  const redirectingRef = useRef(false); // Prevent multiple redirects
  const stuckTimerRef = useRef(null);

  // Use the settled transactions hook to get progress
  // Only fetch if we have a username, we're not ready, and not redirecting
  const shouldFetch = !!(username && !ready && !redirectingRef.current);
  const { 
    transactions: settledTransactions, 
    loading: settledTransactionsLoading, 
    error: settledTransactionsError,
    progress: settledTransactionsProgress 
  } = useSettledTransactions(username, shouldFetch);

  useEffect(() => {
    if (ready || redirectingRef.current) return;
    const interval = setInterval(async () => {
      const status = await checkStatus(); // Should return true if data is ready
      if (status && !redirectingRef.current) {
        setReady(true);
      }
    }, 30000);
    return () => clearInterval(interval);
  }, [ready, checkStatus]);

  // Check if we have settled transactions and redirect automatically
  useEffect(() => {
    if (settledTransactions && settledTransactions.length > 0 && !ready && !redirectingRef.current) {
      redirectingRef.current = true;
      setReady(true);
      
      // Use React Router navigate instead of window.location.href to avoid full page reload
      // This preserves the session and prevents the loop
      setTimeout(() => {
        navigate('/tax-insights', { replace: true });
      }, 300);
    }
  }, [settledTransactions, ready, navigate]);

  // Show skip options ONLY when no transactions are found (loading finished, no error, empty array)
  useEffect(() => {
    // Clear any existing timer
    if (stuckTimerRef.current) {
      clearTimeout(stuckTimerRef.current);
      stuckTimerRef.current = null;
    }

    // Only show options when:
    // 1. Loading has finished (not loading anymore)
    // 2. Either no error, OR error is "no transactions found" (treat as empty state so user can link accounts)
    // 3. No transactions: either empty array or we have the "no transactions found" error
    const isNoTransactionsError = settledTransactionsError &&
      /no transactions found/i.test(settledTransactionsError);
    const hasNoTransactions = (settledTransactions && settledTransactions.length === 0) || isNoTransactionsError;

    if (!settledTransactionsLoading && hasNoTransactions) {
      // Show options after a short delay to let user see the completion state
      stuckTimerRef.current = setTimeout(() => {
        setShowSkipOptions(true);
      }, isNoTransactionsError ? 0 : 2000); // No delay when we came from error state
    } else {
      setShowSkipOptions(false);
    }

    return () => {
      if (stuckTimerRef.current) {
        clearTimeout(stuckTimerRef.current);
      }
    };
  }, [settledTransactionsLoading, settledTransactionsError, settledTransactions]);

  return (
    <div className="onboarding-progress-modal" style={{textAlign: 'center', padding: '4em 2em', maxWidth: '600px', margin: '0 auto'}}>
      {/* Show progress only while loading; hide when we're showing "no transactions" options */}
      {!showSkipOptions && (
        settledTransactionsProgress && settledTransactionsLoading ? (
          <TransactionProgressIndicator 
            progress={settledTransactionsProgress} 
            loading={settledTransactionsLoading}
          />
        ) : (
          // Fallback to simple loading if no progress data yet
          <div className="transaction-progress-container">
            <div className="transaction-progress-card">
              <div className="progress-card-title">Loading Your Tax Insights</div>
              <div className="progress-step-text" style={{ marginBottom: '24px' }}>
                Initializing transaction processing...
              </div>
              <div className="progress-bar-wrapper">
                <div className="progress-bar-container">
                  <div 
                    className="progress-bar-fill"
                    style={{ width: '30%' }}
                  >
                    <div className="progress-bar-shimmer"></div>
                  </div>
                </div>
                <div className="progress-percentage">30%</div>
              </div>
            </div>
          </div>
        )
      )}

      {/* Error message if there's an error - hide for "no accounts found" (timing) and "no transactions found" (we show link-accounts UI instead) */}
      {settledTransactionsError && 
       !settledTransactionsError.toLowerCase().includes('no accounts found') &&
       !/no transactions found/i.test(settledTransactionsError) && (
        <div style={{
          marginTop: '24px',
          padding: '16px',
          background: 'rgba(220, 53, 69, 0.1)',
          borderRadius: '8px',
          border: '1px solid #dc3545',
          color: '#721c24',
          maxWidth: 600,
          marginLeft: 'auto',
          marginRight: 'auto'
        }}>
          {settledTransactionsError}
        </div>
      )}

      {/* Show skip options if stuck or error */}
      {showSkipOptions && (
        <div style={{
          marginTop: '32px',
          padding: '24px',
          background: '#f8f9fa',
          borderRadius: '12px',
          border: '1px solid #e9ecef',
          maxWidth: 500,
          marginLeft: 'auto',
          marginRight: 'auto'
        }}>
          <p style={{
            marginBottom: '20px',
            color: '#495057',
            fontSize: '15px',
            fontWeight: 500
          }}>
            {hasAccounts 
              ? "No taxable transactions found? You can continue to the dashboard or connect more accounts."
              : "No accounts found. Connect your accounts to get tax insights."
            }
          </p>
          
          <div style={{
            display: 'flex',
            gap: '10px',
            flexDirection: 'row',
            justifyContent: 'center',
            flexWrap: 'wrap'
          }}>
            {hasAccounts ? (
              <>
                <button
                  onClick={() => {
                    if (onConnectAccounts) {
                      onConnectAccounts();
                    }
                  }}
                  style={{
                    background: 'linear-gradient(135deg, #2a9d8f 0%, #1a7a6f 100%)',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '6px',
                    padding: '8px 16px',
                    fontSize: '13px',
                    fontWeight: 600,
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    boxShadow: '0 2px 6px rgba(42,157,143,0.3)'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.transform = 'translateY(-1px)';
                    e.target.style.boxShadow = '0 4px 10px rgba(42,157,143,0.4)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.transform = 'translateY(0)';
                    e.target.style.boxShadow = '0 2px 6px rgba(42,157,143,0.3)';
                  }}
                >
                  Connect More Accounts
                </button>
                <button
                  onClick={() => {
                    if (onContinueToDashboard) {
                      onContinueToDashboard();
                    } else {
                      navigate('/tax-insights', { replace: true });
                    }
                  }}
                  style={{
                    background: '#fff',
                    color: '#2a9d8f',
                    border: '2px solid #2a9d8f',
                    borderRadius: '6px',
                    padding: '8px 16px',
                    fontSize: '13px',
                    fontWeight: 600,
                    cursor: 'pointer',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.background = '#f0f9f7';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.background = '#fff';
                  }}
                >
                  Continue to Dashboard
                </button>
              </>
            ) : (
              <button
                onClick={() => {
                  if (onConnectAccounts) {
                    onConnectAccounts();
                  }
                }}
                style={{
                  background: 'linear-gradient(135deg, #2a9d8f 0%, #1a7a6f 100%)',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '6px',
                  padding: '8px 16px',
                  fontSize: '13px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  boxShadow: '0 2px 6px rgba(42,157,143,0.3)'
                }}
                onMouseEnter={(e) => {
                  e.target.style.transform = 'translateY(-1px)';
                  e.target.style.boxShadow = '0 4px 10px rgba(42,157,143,0.4)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = '0 2px 6px rgba(42,157,143,0.3)';
                }}
              >
                Connect Accounts to Get Tax Insights
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default OnboardingProgress; 